import type { TUseRequestUpdaterProps } from 'features/request/useRequest';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { PriceListRatioRowData, UerWorkOrderPriceListRatioQuery } from 'gql/graphql';
import { RatioTypeEnum } from 'gql/graphql';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { gqlWorkOrderPriceListRatio } from '../../workOrderQueries';

export const getWhere = (filters: any) =>
    Object.keys(filters).reduce((where: any, fieldName: string) => {
        const checkedValues = filters[fieldName];

        if (checkedValues?.length) {
            where[fieldName] = {
                or: checkedValues.map((item: string) => ({
                    eq: item,
                })),
            };
        }

        return where;
    }, {});

interface IUseWorkOrderPriceListRatioData {
    filters: any;
    ratioType: RatioTypeEnum;
    uerId: number;
}

const initialState: Record<RatioTypeEnum, PriceListRatioRowData[] | null> = {
    [RatioTypeEnum.ConstraintCoef]: null,
    [RatioTypeEnum.TechPartCoef]: null,
    [RatioTypeEnum.DismantlingCoef]: null,
};

const requestId = 'gqlWorkOrderPriceListRatio';

export const useWorkOrderPriceListRatioData = (props: IUseWorkOrderPriceListRatioData) => {
    const { filters, ratioType, uerId } = props;
    const dispatch = useDispatch();
    const [data, setData] = useState(initialState);
    const [responseRatioType, setResponseRatioType] = useState<RatioTypeEnum>();

    const [coefficientsReset] = useResetStoreData({ requestId });

    const reset = useCallback(() => {
        coefficientsReset(undefined);
    }, [coefficientsReset]);

    const variables = useMemo(
        () => ({
            workOrderPriceId: uerId,
            where: { ratioType: { eq: ratioType }, ...getWhere(filters[ratioType]) },
        }),
        [uerId, ratioType, filters],
    );

    const onSuccess = useCallback(
        (depsVars: TUseRequestUpdaterProps<UerWorkOrderPriceListRatioQuery>) => {
            setData({
                ...initialState,
                [ratioType]: depsVars?.payload?.uerWorkOrderPriceListRatio?.items || [],
            });
            setResponseRatioType(ratioType);
            reset();
        },
        [ratioType, reset],
    );

    const [, coefficientsStatus, , coefficientsMakeRequest] = useRequest({
        requestId,
        query: gqlWorkOrderPriceListRatio,
        withEffect: false,
        dependenciesUpdater: onSuccess,
    });

    const request = useCallback(() => {
        dispatch(coefficientsMakeRequest(variables));
    }, [coefficientsMakeRequest, dispatch, variables]);

    useEffect(() => {
        setData(initialState);
        reset();
        request();
    }, [request, reset, variables]);

    return { request, reset, data, coefficientsStatus, responseRatioType };
};
